import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Users, Search, CheckCircle, KeyRound, Pencil, Trash2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { EmptyState } from '../components/ui/empty-state';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Modal } from '../components/ui/modal';
import { StatusBadge } from '../components/ui/status-badge';
import { AdminUserDto, UpdateAdminUserRequest } from '../models';
import { confirmUserEmail, deleteUser, getApiError, getUsers, setUserPassword, updateUser } from '../services/api';

const roleDefinitions = [
  { key: 'platform_operator', label: 'Операторы платформы', shortLabel: 'Операторы' },
  { key: 'admin', label: 'Администраторы', shortLabel: 'Администраторы' },
  { key: 'dispatcher', label: 'Диспетчеры', shortLabel: 'Диспетчеры' },
  { key: 'medic', label: 'Медицинские работники', shortLabel: 'Медработники' },
  { key: 'driver', label: 'Водители', shortLabel: 'Водители' },
  { key: 'external', label: 'Внешние сервисы', shortLabel: 'Внешние сервисы' },
] as const;

const roleLabels = new Map<string, string>(roleDefinitions.map((role) => [role.key, role.label]));
const isDriverOnly = (user: AdminUserDto) => user.roles.length > 0 && user.roles.every((role) => role === 'driver');
const belongsToRole = (user: AdminUserDto, role: string) => role === '__without_role'
  ? user.roles.length === 0
  : user.roles.includes(role);

export const UsersPage = () => {
  const [items, setItems] = useState<AdminUserDto[]>();
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [activeRole, setActiveRole] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordModalUser, setPasswordModalUser] = useState<AdminUserDto>();
  const [passwordValue, setPasswordValue] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [editUser, setEditUser] = useState<AdminUserDto>();
  const [editForm, setEditForm] = useState<UpdateAdminUserRequest>();
  const [deleteCandidate, setDeleteCandidate] = useState<AdminUserDto>();
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    setError('');
    getUsers().then((r) => setItems(r.data.data)).catch((e) => setError(getApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const roleTabs = useMemo(() => {
    const knownRoles = new Set(roleDefinitions.map((role) => role.key as string));
    const unknownRoles = Array.from(new Set((items || []).flatMap((user) => user.roles)))
      .filter((role) => !knownRoles.has(role))
      .sort((left, right) => left.localeCompare(right, 'ru'));
    const tabs = [
      ...roleDefinitions.map((role) => ({ ...role, count: (items || []).filter((user) => user.roles.includes(role.key)).length })),
      ...unknownRoles.map((role) => ({ key: role, label: role, shortLabel: role, count: (items || []).filter((user) => user.roles.includes(role)).length })),
    ];
    if ((items || []).some((user) => user.roles.length === 0)) {
      tabs.push({ key: '__without_role', label: 'Без роли', shortLabel: 'Без роли', count: (items || []).filter((user) => user.roles.length === 0).length });
    }
    return tabs;
  }, [items]);

  useEffect(() => {
    if (!roleTabs.length) return;
    if (!roleTabs.some((role) => role.key === activeRole)) {
      setActiveRole(roleTabs.find((role) => role.count > 0)?.key || roleTabs[0].key);
    }
  }, [activeRole, roleTabs]);

  const handleConfirmEmail = async (userId: number) => {
    setError('');
    try {
      await confirmUserEmail(userId);
      setSuccess('Email подтверждён');
      load();
    } catch (e) {
      setError(getApiError(e));
    }
  };

  const handleSetPassword = async () => {
    if (!passwordModalUser || passwordValue.length < 6 || passwordValue !== passwordConfirmation) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await setUserPassword(passwordModalUser.id, passwordValue);
      setSuccess('Пароль установлен');
      closePasswordModal();
      load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const closePasswordModal = () => {
    setPasswordModalUser(undefined);
    setPasswordValue('');
    setPasswordConfirmation('');
    setPasswordVisible(false);
  };

  const openPasswordModal = (user: AdminUserDto) => {
    if (isDriverOnly(user)) return;
    setError('');
    setSuccess('');
    setPasswordModalUser(user);
    setPasswordValue('');
    setPasswordConfirmation('');
    setPasswordVisible(false);
  };

  const openEdit = (user: AdminUserDto) => {
    setEditUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      middleName: user.middleName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      emailConfirmed: user.emailConfirmed,
      isDismissed: user.isDismissed,
    });
  };

  const handleUpdate = async () => {
    if (!editUser || !editForm || !editForm.firstName.trim() || !editForm.lastName.trim()) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await updateUser(editUser.id, editForm);
      setSuccess('Данные пользователя обновлены');
      setEditUser(undefined);
      setEditForm(undefined);
      load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setSubmitting(true);
    setError('');
    setSuccess('');
    try {
      await deleteUser(deleteCandidate.id);
      setSuccess(`Пользователь #${deleteCandidate.id} удалён`);
      setDeleteCandidate(undefined);
      load();
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = useMemo(
    () => (items || []).filter((u) => belongsToRole(u, activeRole)).filter((u) => {
      const q = deferredQuery.trim().toLowerCase();
      if (!q) return true;
      return (u.email || '').toLowerCase().includes(q)
        || u.firstName.toLowerCase().includes(q)
        || u.lastName.toLowerCase().includes(q)
        || (u.phoneNumber || '').toLowerCase().includes(q)
        || String(u.id).includes(q);
    }),
    [activeRole, deferredQuery, items],
  );

  const activeRoleDefinition = roleTabs.find((role) => role.key === activeRole);
  const passwordMismatch = passwordConfirmation.length > 0 && passwordValue !== passwordConfirmation;

  return (
    <AppLayout title="Пользователи">
      <PageHeader title="Пользователи" description="Учётные записи разделены по ролям — без общего смешанного списка." />
      {error && <div className="mb-4 rounded-lg border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-lg border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="border-b border-border bg-muted/20 px-4 pt-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-3" role="tablist" aria-label="Роли пользователей">
            {roleTabs.map((role) => (
              <button
                key={role.key}
                type="button"
                role="tab"
                aria-selected={activeRole === role.key}
                aria-controls="users-role-panel"
                onClick={() => { setActiveRole(role.key); setQuery(''); }}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 ${activeRole === role.key ? 'border-primary bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'}`}
              >
                {role.shortLabel}
                <span className={`rounded-full px-2 py-0.5 font-mono text-[11px] ${activeRole === role.key ? 'bg-primary-foreground/15 text-primary-foreground' : 'bg-muted text-foreground'}`}>{role.count}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-border p-4">
          <div>
            <p className="font-semibold text-foreground">{activeRoleDefinition?.label || 'Пользователи'}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{activeRoleDefinition?.count ?? 0} в этой роли</p>
          </div>
          <label className="ml-auto flex w-full max-w-sm items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20" htmlFor="control-center-user-search">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              id="control-center-user-search"
              type="search"
              name="control-center-user-search"
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Поиск: ${activeRoleDefinition?.shortLabel?.toLowerCase() || 'пользователи'}`}
              aria-label="Найти пользователя в выбранной роли"
              className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
          </label>
        </div>

        <div id="users-role-panel" role="tabpanel">
        {!items && !error ? <LoadingState /> : items && filtered.length === 0 ? (
          <EmptyState icon={<Users className="h-8 w-8" />} title={query ? 'Пользователи не найдены' : 'Нет пользователей'} description={query ? 'Измените поисковый запрос.' : 'Пользователи появятся после регистрации.'} />
        ) : items ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Email / телефон</th>
                  <th className="px-4 py-3 font-medium">ФИО</th>
                  <th className="px-4 py-3 text-center font-medium">Учётная запись</th>
                  <th className="px-4 py-3 font-medium">Роли</th>
                  <th className="px-4 py-3 text-right font-medium">Действия</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} className="border-b border-border/60 transition-colors last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-4 font-mono text-xs text-muted-foreground">#{u.id}</td>
                    <td className="px-4 py-4">
                      <p className="font-mono text-xs text-foreground">{u.email || '—'}</p>
                      {u.phoneNumber && <p className="mt-1 text-xs text-muted-foreground">{u.phoneNumber}</p>}
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-medium text-foreground">{u.lastName} {u.firstName}</p>
                      {u.middleName && <p className="text-xs text-muted-foreground">{u.middleName}</p>}
                      <div className="mt-1">
                        <StatusBadge label={u.isDismissed ? 'Доступ закрыт' : 'Активен'} variant={u.isDismissed ? 'default' : 'success'} />
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {isDriverOnly(u)
                        ? <StatusBadge label="Не требуется" variant="default" />
                        : u.emailConfirmed
                          ? <StatusBadge label="Email подтверждён" variant="success" />
                          : <button type="button" onClick={() => handleConfirmEmail(u.id)} className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary/40">
                            <CheckCircle className="h-3.5 w-3.5" /> Подтвердить
                          </button>
                      }
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.roles.map((r) => (
                          <span key={r} className="rounded-md bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-primary">{roleLabels.get(r) || r}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(u)} title="Редактировать" className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary/40">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {!isDriverOnly(u) && (
                          <button type="button" onClick={() => openPasswordModal(u)} className="flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs text-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-primary/40">
                            <KeyRound className="h-3.5 w-3.5" /> Пароль
                          </button>
                        )}
                        <button type="button" onClick={() => setDeleteCandidate(u)} disabled={u.roles.includes('platform_operator')} title={u.roles.includes('platform_operator') ? 'Системного оператора удалить нельзя' : 'Удалить'} className="flex h-8 w-8 items-center justify-center rounded-md border border-destructive/20 bg-destructive/5 text-destructive transition-colors hover:bg-destructive/10 focus-visible:ring-2 focus-visible:ring-destructive/40 disabled:cursor-not-allowed disabled:opacity-30">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        </div>
      </div>

      <Modal
        open={Boolean(editUser && editForm)}
        onClose={() => { setEditUser(undefined); setEditForm(undefined); }}
        title={`Редактирование пользователя #${editUser?.id ?? ''}`}
        maxWidth="max-w-xl"
        footer={(
          <>
            <Button variant="ghost" onClick={() => { setEditUser(undefined); setEditForm(undefined); }} disabled={submitting}>Отмена</Button>
            <Button onClick={handleUpdate} disabled={submitting || !editForm?.firstName.trim() || !editForm?.lastName.trim()}>
              {submitting ? 'Сохранение...' : 'Сохранить'}
            </Button>
          </>
        )}
      >
        {editForm && editUser && (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="Фамилия *" value={editForm.lastName} maxLength={50} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} />
              <Input label="Имя *" value={editForm.firstName} maxLength={50} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} />
              <Input label="Отчество" value={editForm.middleName || ''} maxLength={50} onChange={(e) => setEditForm({ ...editForm, middleName: e.target.value })} />
              <Input label="Телефон" value={editForm.phoneNumber || ''} maxLength={20} onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })} />
              <div className="sm:col-span-2">
                <Input label={isDriverOnly(editUser) ? 'Email (необязательно)' : 'Email'} type="email" name="edit-user-email" autoComplete="off" value={editForm.email || ''} maxLength={128} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
            </div>

            <div className="rounded-lg border border-border bg-muted/20 p-4 text-sm">
              <p className="text-muted-foreground">Организация: <span className="font-mono text-foreground">Tenant #{editUser.tenantId}</span></p>
              <p className="mt-1 text-muted-foreground">Роли: <span className="text-foreground">{editUser.roles.join(', ') || '—'}</span></p>
              <p className="mt-2 text-xs text-muted-foreground">Организация и роли меняются через профиль соответствующей роли, чтобы не нарушить связи.</p>
            </div>

            {isDriverOnly(editUser) ? (
              <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-4 text-sm">
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <div>
                  <p className="font-medium text-foreground">Личный кабинет не используется</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Для водителя не требуется пароль и подтверждение email. Доступ к системе ему не выдаётся.</p>
                </div>
              </div>
            ) : (
              <label className="flex cursor-pointer items-center gap-3 text-sm text-foreground">
                <input type="checkbox" checked={editForm.emailConfirmed} disabled={!editForm.email?.trim()} onChange={(e) => setEditForm({ ...editForm, emailConfirmed: e.target.checked })} className="h-4 w-4 rounded border-border accent-primary" />
                Email подтверждён
              </label>
            )}
            <label className="flex cursor-pointer items-center gap-3 text-sm text-foreground">
              <input type="checkbox" checked={editForm.isDismissed} disabled={editUser.roles.includes('platform_operator')} onChange={(e) => setEditForm({ ...editForm, isDismissed: e.target.checked })} className="h-4 w-4 rounded border-border accent-primary disabled:cursor-not-allowed disabled:opacity-50" />
              Закрыть пользователю доступ в систему
            </label>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(deleteCandidate)}
        onClose={() => setDeleteCandidate(undefined)}
        title="Удаление пользователя"
        maxWidth="max-w-md"
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDeleteCandidate(undefined)} disabled={submitting}>Отмена</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting}>
              {submitting ? 'Удаление...' : 'Удалить'}
            </Button>
          </>
        )}
      >
        {deleteCandidate && (
          <div className="space-y-3 text-sm">
            <p className="text-foreground">Удалить <strong>{deleteCandidate.lastName} {deleteCandidate.firstName}</strong> ({deleteCandidate.email || `#${deleteCandidate.id}`})?</p>
            <p className="text-muted-foreground">Вход будет заблокирован, а пользователь исчезнет из списка. Email и телефон можно будет использовать заново. Завершённые осмотры и журналы сохранятся.</p>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(passwordModalUser)}
        onClose={closePasswordModal}
        title="Установить новый пароль"
        maxWidth="max-w-md"
        footer={(
          <>
            <Button type="button" variant="ghost" onClick={closePasswordModal} disabled={submitting}>Отмена</Button>
            <Button type="submit" form="set-user-password-form" disabled={passwordValue.length < 6 || passwordMismatch || !passwordConfirmation || submitting}>
              {submitting ? 'Сохранение...' : 'Сохранить пароль'}
            </Button>
          </>
        )}
      >
        {passwordModalUser && (
          <form id="set-user-password-form" autoComplete="off" onSubmit={(event) => { event.preventDefault(); handleSetPassword(); }} className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3">
              <p className="font-medium text-foreground">{passwordModalUser.lastName} {passwordModalUser.firstName}</p>
              <p className="mt-1 text-xs text-muted-foreground">{passwordModalUser.email || `Пользователь #${passwordModalUser.id}`}</p>
            </div>
            <input type="text" name="username" value={passwordModalUser.email || String(passwordModalUser.id)} autoComplete="username" readOnly tabIndex={-1} className="sr-only" aria-hidden="true" />
            <div className="relative">
              <Input
                label="Новый пароль"
                type={passwordVisible ? 'text' : 'password'}
                name="new-password"
                autoComplete="new-password"
                value={passwordValue}
                minLength={6}
                onChange={(event) => setPasswordValue(event.target.value)}
                error={passwordValue.length > 0 && passwordValue.length < 6 ? 'Минимум 6 символов' : undefined}
                autoFocus
                className="pr-11"
              />
              <button type="button" onClick={() => setPasswordVisible((visible) => !visible)} aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'} className="absolute right-2 top-[30px] flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-primary/40">
                {passwordVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Input
              label="Повторите пароль"
              type={passwordVisible ? 'text' : 'password'}
              name="confirm-new-password"
              autoComplete="new-password"
              value={passwordConfirmation}
              minLength={6}
              onChange={(event) => setPasswordConfirmation(event.target.value)}
              error={passwordMismatch ? 'Пароли не совпадают' : undefined}
            />
            <p className="text-xs leading-relaxed text-muted-foreground">Сохранённые браузером логины не подставляются: форма смены пароля изолирована от поиска пользователей.</p>
          </form>
        )}
      </Modal>
    </AppLayout>
  );
};
